import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import Layout from '../../components/layout/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchLecturerDetail, LecturerDetail } from '../../services/lecturerApi';
import { getPostList, PostListItem } from '../../services/postApi';
import { useUserInfo } from '../../hooks/useUserInfo';
import LoginModal from '../../components/modals/LoginModal';

const CoffeeChatDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('introduction');
  const [isTabSticky, setIsTabSticky] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  
  // 사용자 정보 훅
  const { userInfo } = useUserInfo();
  
  // 로그인 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingPostId, setPendingPostId] = useState<number | null>(null);
  
  // 데이터 상태 관리
  const [lecturerDetail, setLecturerDetail] = useState<LecturerDetail | null>(null);
  const [qnaList, setQnaList] = useState<PostListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Q&A 페이징 상태
  const [qnaPage, setQnaPage] = useState(0);
  const [qnaTotalPages, setQnaTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [dummyPage, setDummyPage] = useState(0); // 더미 데이터용 페이지
  const QNA_PAGE_SIZE = 4;
  
  // 더미 데이터 생성 (ID 28용)
  const dummyData = useMemo(() => {
    return [...Array(9)].map((_, index) => ({
      post_id: index + 1,
      title: `멘토님께 질문이 있습니다 #${index + 1}`,
      writer_name: `김*닥${index + 1}`,
      created_at: `2025-01-${String(19 + index).padStart(2, '0')}T${String(14 + index).padStart(2, '0')}:30:00.000Z`,
      reply_cnt: index,
      view_cnt: 15 + index * 3
    }));
  }, []);
  
  // ID 28의 현재 표시할 더미 데이터
  const currentDummyData = useMemo(() => {
    if (id !== '28') return [];
    const startIndex = 0;
    const endIndex = (dummyPage + 1) * QNA_PAGE_SIZE;
    return dummyData.slice(startIndex, endIndex);
  }, [id, dummyPage, dummyData]);
  
  // 이미지 로딩 상태 관리
  const [imageLoadStates, setImageLoadStates] = useState<{[key: string]: boolean}>({});
  
  // 탭 데이터
  const tabs = useMemo(() => [
    { id: 'introduction', label: '멘토 소개' },
    { id: 'qna', label: 'Q&A' }
  ], []);
  
  // 탭과 섹션 참조
  const tabSectionRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // 탭 섹션의 원래 위치를 저장 (변하지 않는 값)
  const originalTabPositionRef = useRef<number>(0);

  // 페이지 로드 시 스크롤 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 초기 탭 위치 저장 (데이터 로딩 완료 후)
  useEffect(() => {
    if (!isLoading && tabSectionRef.current && originalTabPositionRef.current === 0) {
      // 약간의 지연을 두어 렌더링이 완료된 후 위치 계산
      setTimeout(() => {
        if (tabSectionRef.current) {
          originalTabPositionRef.current = tabSectionRef.current.offsetTop;
          // /// console.log('Tab section original position:', originalTabPositionRef.current);
        }
      }, 100);
    }
  }, [isLoading]);

  // 탭 인디케이터 위치 업데이트 (클래스 레크처와 동일)
  const updateIndicatorPosition = useCallback((tabId: string) => {
    const activeTabElement = tabRefs.current[tabId];
    const tabListElement = tabListRef.current;
    
    if (activeTabElement && tabListElement) {
      const tabRect = activeTabElement.getBoundingClientRect();
      const listRect = tabListElement.getBoundingClientRect();
      
      const left = tabRect.left - listRect.left;
      const width = tabRect.width;
      
      // 헤더 상태일 때 패딩 영역보다 왼쪽에 있으면 숨김
      let shouldHide = false;
      if (isTabSticky) {
        const containerPadding = window.innerWidth > 1024 ? 80 : window.innerWidth > 768 ? 64 : 24;
        shouldHide = left + width <= containerPadding; // 탭 전체가 패딩 영역에 있으면 숨김
      }
      
      // 헤더 상태에서는 transition 없이 즉시 나타남
      const indicatorElement = document.querySelector('.tab-indicator') as HTMLElement;
      if (indicatorElement && isTabSticky) {
        indicatorElement.style.transition = 'none';
        setTimeout(() => {
          if (indicatorElement) {
            indicatorElement.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          }
        }, 10);
      }
      
      setIndicatorStyle({
        left: shouldHide ? -1000 : left,
        width: shouldHide ? 0 : width
      });
    }
  }, [isTabSticky]);

  // 스크롤 이벤트 핸들러 (클래스 레크처와 동일)
  useEffect(() => {
    const handleScroll = () => {
      if (!tabSectionRef.current || originalTabPositionRef.current === 0) return;

      const scrollY = window.scrollY;
      const headerHeight = 80; // 헤더 높이 정의
      
      // 원래 탭 위치 사용 (변하지 않는 값)
      const tabSectionTop = originalTabPositionRef.current;
      
      // 탭 영역을 완전히 지나친 후에 sticky 활성화
      const tabSectionBottom = tabSectionTop + 56; // 탭 높이 56px
      const shouldBeSticky = scrollY >= tabSectionBottom - headerHeight;
      
      if (shouldBeSticky !== isTabSticky) {
        setIsTabSticky(shouldBeSticky);
        // sticky 상태 변경 시 인디케이터 위치 업데이트
        setTimeout(() => {
          updateIndicatorPosition(activeTab);
        }, 50);
      }

      // 현재 보이는 섹션 찾기
      const sectionOffsets = tabs.map(tab => {
        const element = sectionRefs.current[tab.id];
        if (!element) return { id: tab.id, offset: 0 };
        return { id: tab.id, offset: element.offsetTop };
      });

      // 현재 스크롤 위치에서 가장 적합한 섹션 찾기
      const offsetForActiveSection = shouldBeSticky ? headerHeight + 56 + 10 : headerHeight + 60; // sticky일 때는 헤더+탭 높이+여백
      const targetScrollPosition = scrollY + offsetForActiveSection;
      
      let currentSection = sectionOffsets[0];
      for (let i = 0; i < sectionOffsets.length; i++) {
        if (sectionOffsets[i].offset <= targetScrollPosition) {
          currentSection = sectionOffsets[i];
        } else {
          break;
        }
      }

      if (currentSection.id !== activeTab) {
        setActiveTab(currentSection.id);
      }
    };

    // 초기 실행
    const timer = setTimeout(() => {
      if (originalTabPositionRef.current === 0 && tabSectionRef.current) {
        originalTabPositionRef.current = tabSectionRef.current.offsetTop;
      }
      handleScroll();
    }, 100);

    // 스로틀링을 위한 requestAnimationFrame 사용
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    return () => {
      window.removeEventListener('scroll', scrollHandler);
      clearTimeout(timer);
    };
  }, [activeTab, tabs, isTabSticky, updateIndicatorPosition]);

  // activeTab 변경 시 인디케이터 위치 업데이트
  useEffect(() => {
    updateIndicatorPosition(activeTab);
  }, [activeTab, updateIndicatorPosition, isTabSticky]);

  // 초기 렌더링 후 인디케이터 위치 설정
  useEffect(() => {
    const timer = setTimeout(() => {
      updateIndicatorPosition(activeTab);
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab, updateIndicatorPosition]);

  // 탭 클릭 시 해당 섹션으로 스크롤 (클래스 레크처와 동일)
  const handleTabClick = useCallback((tabId: string) => {
    // 먼저 activeTab 상태 업데이트
    setActiveTab(tabId);
    
    const element = sectionRefs.current[tabId];
    if (element) {
      const headerHeight = 80;
      const tabHeight = 56;
      const offset = isTabSticky ? headerHeight + tabHeight + 10 : headerHeight + 20; // 항상 헤더 높이 고려, sticky일 때는 탭 높이도 추가
      
      const elementRect = element.getBoundingClientRect();
      const elementPosition = window.pageYOffset + elementRect.top - offset;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  }, [isTabSticky]);

  // API 데이터 로드
  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError('강연자 ID가 없습니다.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        // 상태 초기화
        setQnaList([]);
        setQnaPage(0);
        setQnaTotalPages(0);
        setDummyPage(0);

        // 모든 ID에 대해 실제 API 호출 시도
        const [lecturerResponse, qnaResponse] = await Promise.all([
          fetchLecturerDetail(id),
          getPostList('coffee-chat', 0, QNA_PAGE_SIZE, undefined, undefined, undefined, undefined, parseInt(id))
        ]);

        setLecturerDetail(lecturerResponse.data);
        setQnaList(qnaResponse.data.content);
        setQnaTotalPages(qnaResponse.data.totalPages);
        setQnaPage(0);

      } catch (error) {
        /// console.error('데이터 로드 실패:', error);
        // setError('데이터를 불러오는데 실패했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Q&A 더보기 로드 함수
  const loadMoreQna = async () => {
    // 실제 API 데이터가 있는 경우 API 더보기
    if (qnaList.length > 0) {
      if (!id || isLoadingMore || qnaPage + 1 >= qnaTotalPages) return;

      try {
        setIsLoadingMore(true);
        const nextPage = qnaPage + 1;
        
        const qnaResponse = await getPostList(
          'coffee-chat', 
          nextPage, 
          QNA_PAGE_SIZE, 
          undefined, 
          undefined, 
          undefined, 
          undefined, 
          parseInt(id)
        );

        // 기존 데이터에 새 데이터 추가
        setQnaList(prev => [...prev, ...qnaResponse.data.content]);
        setQnaPage(nextPage);

      } catch (error) {
        /// console.error('Q&A 더보기 로드 실패:', error);
      } finally {
        setIsLoadingMore(false);
      }
      return;
    }

    // ID 28이고 실제 API 데이터가 없는 경우에만 더미 데이터 더보기
    if (id === '28' && qnaList.length === 0) {
      const nextPage = dummyPage + 1;
      const maxPage = Math.ceil(dummyData.length / QNA_PAGE_SIZE) - 1;
      
      if (nextPage <= maxPage) {
        setIsLoadingMore(true);
        // 실제 API 호출처럼 약간의 지연 시뮬레이션
        setTimeout(() => {
          setDummyPage(nextPage);
          setIsLoadingMore(false);
        }, 500);
      }
    }
  };

  // 멘토 정보 (실제 API 데이터 사용)
  const mentorData = lecturerDetail ? {
    title: lecturerDetail.introduction,
    name: lecturerDetail.name,
    affiliation: lecturerDetail.department || lecturerDetail.introduction,
    image: lecturerDetail.banner_image_url,
    tags: lecturerDetail.tags || []
  } : {
    title: '로딩 중...',
    name: '로딩 중...',
    affiliation: '로딩 중...',
    image: '', // 빈 문자열로 변경해서 스켈레톤이 표시되도록 함
    tags: []
  };

  // 이미지 로드 핸들러
  const handleImageLoad = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: true
    }));
  };

  // 이미지 에러 핸들러
  const handleImageError = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: false
    }));
  };

  // QnA 아이템 클릭 핸들러 - 로그인 확인
  const handleQnaItemClick = useCallback((postId: number) => {
    // 로그인되지 않은 경우 게시글 ID 저장 후 로그인 모달 표시
    if (!userInfo) {
      setPendingPostId(postId);
      setShowLoginModal(true);
      return;
    }
    // 로그인된 경우 페이지 이동
    navigate(`/coffee-chat/post/${postId}`);
  }, [navigate, userInfo]);

  // 질문하기 버튼 클릭 핸들러
  const handleQuestionClick = useCallback(() => {
    if (!id) {
      /// console.error('강연자 ID가 없습니다.');
      return;
    }
    navigate(`/coffee-chat/write/${id}`);
  }, [id, navigate]);

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = useCallback(() => {
    // /// console.log('로그인 성공! 모달을 닫습니다.');
    setShowLoginModal(false);
    
    // 저장된 게시글 ID가 있으면 해당 게시글로 이동
    if (pendingPostId) {
      navigate(`/coffee-chat/post/${pendingPostId}`);
      setPendingPostId(null);
    }
  }, [navigate, pendingPostId]);

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = useCallback(() => {
    setShowLoginModal(false);
    setPendingPostId(null); // 저장된 게시글 ID 초기화
  }, []);

  return (
    <Layout>
      <Container>
        {/* 메인 콘텐츠 컨테이너 */}
        <MainContainer>
          {/* 에러 상태 */}
          {error && (
            <ErrorContainer>
              <ErrorText>{error}</ErrorText>
            </ErrorContainer>
          )}
          
          {/* 멘토 정보 섹션 */}
          <MentorInfoSection>
            {/* 멘토 배너 이미지 */}
            <MentorBanner>
              <MentorBannerImage>
                {(isLoading || !imageLoadStates['mentor-banner']) && (
                  <MentorBannerImageSkeleton />
                )}
                <img 
                  src={mentorData.image || '/images/coffee-chat-default-banner.png'} 
                  alt={mentorData.title}
                  style={{ 
                    display: imageLoadStates['mentor-banner'] && !isLoading ? 'block' : 'none' 
                  }}
                  onLoad={() => handleImageLoad('mentor-banner')}
                  onError={() => handleImageError('mentor-banner')}
                />
              </MentorBannerImage>
            </MentorBanner>

            {/* 멘토 제목과 정보 */}
            <MentorHeader>
              <MentorInfo>
                {isLoading ? (
                  <>
                    <MentorTitleSkeleton />
                    <MentorSubtitleSkeleton />
                  </>
                ) : (
                  <>
                    <MentorTitle>{mentorData.name}</MentorTitle>
                    <MentorSubtitle>
                      {mentorData.affiliation}
                    </MentorSubtitle>
                  </>
                )}
              </MentorInfo>
              <StartButton onClick={handleQuestionClick}>
                <ButtonText>질문하기</ButtonText>
              </StartButton>
            </MentorHeader>
          </MentorInfoSection>

          {/* 탭 네비게이션 */}
          <TabSection ref={tabSectionRef} $isSticky={isTabSticky}>
            <TabContainer>
              <TabList $isSticky={isTabSticky} ref={tabListRef}>
                {tabs.map((tab) => (
                  <TabItem 
                    key={tab.id}
                    ref={(el) => tabRefs.current[tab.id] = el}
                    active={activeTab === tab.id}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    <TabText active={activeTab === tab.id}>{tab.label}</TabText>
                  </TabItem>
                ))}
                <TabIndicator 
                  className="tab-indicator"
                  style={{
                    left: `${indicatorStyle.left}px`,
                    width: `${indicatorStyle.width}px`
                  }}
                />
              </TabList>
              <TabDivider $isSticky={isTabSticky} />
            </TabContainer>
          </TabSection>

          {/* Sticky 상태일 때 콘텐츠 여백 */}
          {isTabSticky && <StickyPlaceholder />}

          {/* 콘텐츠 영역 */}
          <ContentSection>
            {tabs.map((tab) => (
              <ContentItem
                key={tab.id}
                ref={(el) => sectionRefs.current[tab.id] = el}
                id={`section-${tab.id}`}
              >
                {tab.id === 'introduction' && (
                  <IntroductionContent>
                    {isLoading ? (
                      <IntroductionSkeleton>
                        <IntroductionLineSkeleton style={{ width: '100%' }} />
                        <IntroductionLineSkeleton style={{ width: '85%' }} />
                        <IntroductionLineSkeleton style={{ width: '92%' }} />
                        <IntroductionLineSkeleton style={{ width: '78%' }} />
                        <IntroductionLineSkeleton style={{ width: '95%' }} />
                        <IntroductionLineSkeleton style={{ width: '65%' }} />
                      </IntroductionSkeleton>
                    ) : lecturerDetail ? (
                      <>
                        {/* 멘토 소개 이미지 - introduction이 이미지 URL인지 확인하여 표시 */}
                        {lecturerDetail.introduction && (lecturerDetail.introduction.includes('.svg') || 
                         lecturerDetail.introduction.includes('.png') || 
                         lecturerDetail.introduction.includes('.jpg') || 
                         lecturerDetail.introduction.includes('.jpeg') || 
                         lecturerDetail.introduction.includes('.webp')) ? (
                          <IntroductionImage>
                            <img 
                              src={lecturerDetail.introduction} 
                              alt="멘토 소개" 
                            />
                          </IntroductionImage>
                        ) : (
                          <>
                            {/* 기본 소개 이미지 */}
                            <IntroductionImage>
                              <img 
                                src="/images/mentor-introduction.svg" 
                                alt="멘토 소개" 
                              />
                            </IntroductionImage>
                            {/* 멘토 소개 텍스트 */}
                            {lecturerDetail.introduction && (
                              <IntroductionText>
                                {lecturerDetail.introduction}
                              </IntroductionText>
                            )}
                          </>
                        )}
                        {/* 태그 표시 */}
                        {lecturerDetail.tags && lecturerDetail.tags.length > 0 && (
                          <TagContainer>
                            {lecturerDetail.tags.map((tag, index) => (
                              <Tag key={index}>{tag}</Tag>
                            ))}
                          </TagContainer>
                        )}
                      </>
                    ) : (
                      <ErrorText>멘토 정보를 불러올 수 없습니다.</ErrorText>
                    )}
                  </IntroductionContent>
                )}
                
                {tab.id === 'qna' && (
                  <QnaSection>
                    {/* 멘티들이 남긴 질문 헤더 */}
                    <QnaSectionHeader>
                      {isLoading ? (
                        <QnaSectionTitleSkeleton />
                      ) : (
                        <QnaSectionTitle>멘티들이 남긴 질문</QnaSectionTitle>
                      )}
                    </QnaSectionHeader>
                    
                    {isLoading ? (
                      <QnaListContainer>
                        <QnaDivider />
                        {/* Q&A 스켈레톤 4개 */}
                        {[...Array(4)].map((_, index) => (
                          <QnaListItem key={`skeleton-${index}`}>
                            <QnaItemContainer>
                              <QnaItemHeader>
                                <QnaItemTitleSkeleton />
                              </QnaItemHeader>
                              <QnaItemInfo>
                                <QnaItemLeft>
                                  <UserAvatarSkeleton />
                                  <UserNameSkeleton />
                                  <InfoDivider />
                                  <QnaItemDateSkeleton />
                                </QnaItemLeft>
                                <QnaItemRight>
                                  <QnaItemStatSkeleton />
                                  <InfoDivider />
                                  <QnaItemStatSkeleton />
                                </QnaItemRight>
                              </QnaItemInfo>
                            </QnaItemContainer>
                            <QnaDivider />
                          </QnaListItem>
                        ))}
                      </QnaListContainer>
                    ) : currentDummyData.length > 0 || qnaList.length > 0 ? (
                      <QnaListContainer>
                        <QnaDivider />
                        {/* ID가 28이고 실제 API 데이터가 없는 경우에만 더미 데이터 4개씩 표시 */}
                        {id === '28' && qnaList.length === 0 && currentDummyData.map((item) => (
                          <QnaListItem key={`dummy-${item.post_id}`}>
                            <QnaItemContainer 
                              onClick={() => handleQnaItemClick(item.post_id)}
                              $isLoggedIn={!!userInfo}
                            >
                              <QnaItemHeader>
                                <QnaItemTitle>{item.title}</QnaItemTitle>
                              </QnaItemHeader>
                              <QnaItemInfo>
                                <QnaItemLeft>
                                  <UserAvatar>
                                    {!imageLoadStates[`dummy-avatar-${item.post_id}`] && (
                                      <UserAvatarSkeleton />
                                    )}
                                    <img 
                                      src="/images/default-avatar.png" 
                                      alt="사용자"
                                      style={{ 
                                        display: imageLoadStates[`dummy-avatar-${item.post_id}`] ? 'block' : 'none' 
                                      }}
                                      onLoad={() => handleImageLoad(`dummy-avatar-${item.post_id}`)}
                                      onError={() => handleImageError(`dummy-avatar-${item.post_id}`)}
                                    />
                                  </UserAvatar>
                                  <UserName>{item.writer_name}</UserName>
                                  <InfoDivider />
                                  <QnaItemDate>{new Date(item.created_at).toLocaleDateString('ko-KR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false
                                  })}</QnaItemDate>
                                </QnaItemLeft>
                                <QnaItemRight>
                                  <QnaItemStat>답변 {item.reply_cnt}</QnaItemStat>
                                  <InfoDivider />
                                  <QnaItemStat>조회수 {item.view_cnt}</QnaItemStat>
                                </QnaItemRight>
                              </QnaItemInfo>
                            </QnaItemContainer>
                            <QnaDivider />
                          </QnaListItem>
                        ))}
                        
                        {/* 실제 API 데이터 (모든 경우) */}
                        {qnaList.map((post) => (
                          <QnaListItem key={post.post_id}>
                            <QnaItemContainer 
                              onClick={() => handleQnaItemClick(post.post_id)}
                              $isLoggedIn={!!userInfo}
                            >
                              <QnaItemHeader>
                                <QnaItemTitle>{post.title}</QnaItemTitle>
                              </QnaItemHeader>
                              <QnaItemInfo>
                                <QnaItemLeft>
                                  <UserAvatar>
                                    {!imageLoadStates[`api-avatar-${post.post_id}`] && (
                                      <UserAvatarSkeleton />
                                    )}
                                    <img 
                                      src={post?.writer_profile_image_url || '/images/default-avatar.png'} 
                                      alt="사용자"
                                      style={{ 
                                        display: imageLoadStates[`api-avatar-${post.post_id}`] ? 'block' : 'none' 
                                      }}
                                      onLoad={() => handleImageLoad(`api-avatar-${post.post_id}`)}
                                      onError={() => handleImageError(`api-avatar-${post.post_id}`)}
                                    />
                                  </UserAvatar>
                                  <UserName>{post.writer_name}</UserName>
                                  <InfoDivider />
                                  <QnaItemDate>{new Date(post.created_at).toLocaleDateString('ko-KR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false
                                  })}</QnaItemDate>
                                </QnaItemLeft>
                                <QnaItemRight>
                                  <QnaItemStat>답변 {post.reply_cnt}</QnaItemStat>
                                  <InfoDivider />
                                  <QnaItemStat>조회수 {post.view_cnt}</QnaItemStat>
                                </QnaItemRight>
                              </QnaItemInfo>
                            </QnaItemContainer>
                            <QnaDivider />
                          </QnaListItem>
                        ))}
                        
                        {/* 더보기 버튼 - 실제 API 데이터가 있으면 API 기준, 없으면 더미 데이터 기준 */}
                        {((qnaList.length > 0 && qnaPage + 1 < qnaTotalPages) || 
                          (id === '28' && qnaList.length === 0 && (dummyPage + 1) * QNA_PAGE_SIZE < dummyData.length)) && (
                          <LoadMoreContainer>
                            <LoadMoreButton 
                              onClick={loadMoreQna}
                              disabled={isLoadingMore}
                            >
                              <LoadMoreIcon>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                  <path 
                                    d="M8.94 12.94L16 19.88L23.06 12.94" 
                                    stroke="rgba(55, 56, 60, 0.61)" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </LoadMoreIcon>
                            </LoadMoreButton>
                          </LoadMoreContainer>
                        )}
                      </QnaListContainer>
                    ) : (
                      <>
                        <QnaDivider />
                        <EmptyText>아직 Q&A가 없습니다.</EmptyText>
                      </>
                    )}
                  </QnaSection>
                )}
              </ContentItem>
            ))}
          </ContentSection>
        </MainContainer>

        {/* 로그인 모달 */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    </Layout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
`;

/* 메인 컨테이너 */
const MainContainer = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 24px ${({ theme }) => theme.layout.containerPadding} 128px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  
  @media (max-width: 1024px) {
    padding: 24px ${({ theme }) => theme.layout.containerPaddingTablet} 128px;
  }
  
  @media (max-width: 768px) {
    padding: 24px ${({ theme }) => theme.layout.containerPaddingMobile} 100px;
    gap: 48px;
  }
`;

/* 멘토 정보 섹션 */
const MentorInfoSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 32px;
  
  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const MentorBanner = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 3/1;
  min-height: 200px; /* 최소 높이 보장 */
  
  @media (max-width: 768px) {
    aspect-ratio: 2/1;
    min-height: 150px;
  }
`;

const MentorBannerImage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    user-select: none;
    pointer-events: none;
    -webkit-user-drag: none;
    -webkit-user-select: none;
  }
`;

const MentorHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const MentorInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MentorTitle = styled.h1`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.08px;
  letter-spacing: -0.66px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const MentorSubtitle = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26.1px;
  letter-spacing: 0px;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const StartButton = styled.button`
  width: 240px;
  height: 64px;
  padding: 12px 28px;
  background: #448181;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(68, 129, 129, 0.9);
  }
  
  @media (max-width: 768px) {
    align-self: stretch;
    width: 100%;
    height: 56px;
    padding: 12px 24px;
  }
`;

const ButtonText = styled.span`
  color: white;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

/* 콘텐츠 섹션 (클래스 레크처와 동일) */
const ContentSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: -48px; /* 탭 아래 여백 제거 */
`;

const ContentItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  
  &:not(:last-child) {
    margin-bottom: 128px; /* 각 섹션 간의 여백 */
  }
  
  @media (max-width: 768px) {
    &:not(:last-child) {
      margin-bottom: 96px; /* 모바일에서는 조금 줄임 */
    }
  }
`;

// 스켈레톤 UI 컴포넌트
const MentorBannerImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 멘토 정보 스켈레톤
const MentorTitleSkeleton = styled.div`
  height: 38px;
  width: 70%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const MentorSubtitleSkeleton = styled.div`
  height: 26px;
  width: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  margin-top: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// Q&A 섹션 제목 스켈레톤
const QnaSectionTitleSkeleton = styled.div`
  height: 38px;
  width: 280px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 멘토 소개 스켈레톤
const IntroductionSkeleton = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IntroductionLineSkeleton = styled.div`
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// Q&A 아이템 스켈레톤
const QnaItemTitleSkeleton = styled.div`
  height: 26px;
  width: 80%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const UserAvatarSkeleton = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  position: absolute;
  top: 0;
  left: 0;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const UserNameSkeleton = styled.div`
  height: 16px;
  width: 60px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const QnaItemDateSkeleton = styled.div`
  height: 16px;
  width: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const QnaItemStatSkeleton = styled.div`
  height: 16px;
  width: 50px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 콘텐츠 영역 스타일
const IntroductionContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroductionImage = styled.div`
  width: 1200px;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
  
  @media (max-width: 1240px) {
    width: calc(100% - 40px);
    margin: 0 20px;
  }
  
  @media (max-width: 768px) {
    width: calc(100% - 20px);
    margin: 0 10px;
  }
  
  img {
    width: 1320px;
    height: auto;
    display: block;
    object-fit: cover;
    object-position: left top;
    margin-left: 0;
  }
`;

const IntroductionText = styled.p`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
  white-space: pre-wrap;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: rgba(68, 129, 129, 0.1);
  color: #296768;
  border-radius: 16px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20px;
  border: 1px solid rgba(68, 129, 129, 0.2);
`;

const QnaSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

// Q&A 섹션 헤더
const QnaSectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 12px;
  margin-top: 60px;
`;

const QnaSectionTitle = styled.h2`
  color: #296768;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  word-wrap: break-word;
  margin: 0;
`;

// Q&A 목록 컴포넌트
const QnaListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const QnaListItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const QnaItemContainer = styled.div<{ $isLoggedIn?: boolean }>`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
  opacity: ${props => props.$isLoggedIn ? 1 : 0.6};
`;

const QnaItemHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QnaItemTitle = styled.h3`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
  word-wrap: break-word;
  margin: 0;
`;

const QnaItemInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const QnaItemLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const QnaItemRight = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const UserAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(112, 115, 124, 0.08);
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const UserName = styled.span`
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const QnaItemDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const QnaItemStat = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const InfoDivider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const QnaDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

// 더보기 버튼 스타일
const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
`;

const LoadMoreButton = styled.button<{ disabled?: boolean }>`
  height: 32px;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;
  background: none;
  border: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: opacity 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.7;
  }
`;

const LoadMoreIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// 상태 메시지 스타일
const ErrorContainer = styled.div`
  width: 100%;
  padding: 24px;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ErrorText = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  margin: 40px 0;
`;

const EmptyText = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  margin: 40px 0;
`;

/* 탭 섹션 (클래스 레크처와 동일) */
const TabSection = styled.section<{ $isSticky?: boolean }>`
  width: 100%;
  height: 56px;
  background: white;
  z-index: 1000;
  transition: all 0.2s ease;
  
  ${props => props.$isSticky && `
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: white;
  `}
`;

const TabContainer = styled.div`
  width: 100%;
  height: 56px;
  position: relative;
`;

const TabList = styled.div<{ $isSticky?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 48px;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.containerPadding} 0 ${props => props.$isSticky ? ({ theme }) => theme.layout.containerPadding : '0'};
  height: 56px;
  
  /* 스크롤바 숨기기 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingTablet} 0 ${props => props.$isSticky ? ({ theme }) => theme.layout.containerPaddingTablet : '0'};
  }
  
  @media (max-width: 768px) {
    gap: 24px;
    padding: 0 ${({ theme }) => theme.layout.containerPaddingMobile} 0 ${props => props.$isSticky ? ({ theme }) => theme.layout.containerPaddingMobile : '0'};
  }
`;

const TabItem = styled.div<{ active?: boolean }>`
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  padding: 0;
  height: 56px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    height: 56px;
  }
`;

const TabText = styled.span<{ active?: boolean }>`
  color: ${props => props.active ? '#296768' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.24px;
  transition: color 0.2s ease;
  
  ${TabItem}:hover & {
    color: #296768;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 22px;
  }
`;

const TabIndicator = styled.div`
  position: absolute;
  bottom: 1px;
  height: 2px;
  background: #296768;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: left;
  z-index: 1;
  
  /* 컨테이너 밖으로 나가면 숨김 */
  overflow: hidden;
`;

const TabDivider = styled.div<{ $isSticky?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
  display: ${props => props.$isSticky ? 'none' : 'block'};
`;

/* Sticky 플레이스홀더 */
const StickyPlaceholder = styled.div`
  height: 56px;
  width: 100%;
`;

export default CoffeeChatDetail;
